import axios from 'axios'
import * as Sentry from '@sentry/vue'

const RETRY_DELAY_MS = 1000

let pageLeaving = false

window.addEventListener('pagehide', () => {
  pageLeaving = true
})

window.addEventListener('pageshow', () => {
  pageLeaving = false
})

/**
 * True while the page is being navigated away from or sitting in the background.
 * Browsers (Safari especially) cancel in-flight requests at that point, which axios
 * reports as "Network Error" even though nothing is actually wrong.
 */
export const isPageInactive = () => pageLeaving || document.visibilityState === 'hidden'

/**
 * An axios error where no response came back at all (dropped connection, request
 * cancelled by the browser, timeout). Deliberate cancellations are excluded.
 */
export const isNetworkError = (error) => axios.isAxiosError(error) && !error.response && !axios.isCancel(error)

/**
 * A split-off chunk of the site's JavaScript, or its CSS, that didn't download. Chrome, Safari
 * and Firefox each word the script failure differently, and Vite reports the CSS one itself
 */
export const isChunkLoadError = (error) =>
  error instanceof Error && /dynamically imported module|Importing a module script failed|Unable to preload CSS/i.test(error.message)

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms))

/**
 * Reload the page after a chunk failed to download. Safari remembers a failed preload for the
 * whole tab, reloads included, so download the theme's preloaded files again first: a plain
 * fetch replaces the remembered failure. Reload anyway if that takes more than a few seconds
 */
export const reloadAfterChunkError = async () => {
  const urls = [...document.querySelectorAll('link[rel="modulepreload"], link[rel="preload"], link[rel="stylesheet"]')]
    .map((link) => link.href)
    .filter((href) => href && new URL(href, window.location.href).pathname.startsWith('/wp-content/themes/capes/build/'))

  await Promise.race([
    Promise.allSettled([...new Set(urls)].map((url) => fetch(url))),
    wait(3000),
  ])

  window.location.reload()
}

/**
 * Hold network failures for a moment before rejecting, and retry GET requests once.
 * If the user is leaving the page it is normally gone before the delay ends, so the
 * failure is never seen; on a flaky connection the retry usually succeeds. Timeouts
 * (ECONNABORTED) aren't retried: the server is slow, and asking again adds to its load.
 */
export const installNetworkRetry = () => {
  axios.interceptors.response.use(undefined, async (error) => {
    if (!isNetworkError(error) || !error.config) {
      return Promise.reject(error)
    }

    await wait(RETRY_DELAY_MS)

    const config = error.config

    if (pageLeaving || config.networkRetried || config.method !== 'get' || error.code === 'ECONNABORTED') {
      return Promise.reject(error)
    }

    config.networkRetried = true

    return axios.request(config)
  })
}

/**
 * Report a failed load to Sentry, ignoring failures caused by the page going away.
 * Connection failures are grouped into a single warning-level issue.
 */
export const reportLoadError = (error) => {
  if (isPageInactive()) {
    return
  }

  if (isNetworkError(error)) {
    Sentry.captureException(error, { level: 'warning', fingerprint: ['network-error'] })
    return
  }

  Sentry.captureException(error)
}
